import { useConnection } from "@solana/wallet-adapter-react";
import type { StakingPosition } from "@stepfinance/staking";
import { useEffect, useMemo, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLendingContext } from "../../contexts/lending";
import { useMarkets } from "../../contexts/market";
import { APT_MINT } from "../../models/apricot/ids";

export const useApricotStaking = () => {
  const { connection } = useConnection();
  const { connected, publicKey } = useEnrichedWallet();
  const { marketsInitialised, priceInUSD } = useMarkets();

  const {
    platforms: { apricot },
  } = useLendingContext();
  const [apricotStaking, setApricotStaking] = useState<StakingPosition[]>([]);

  const apricotStakingRecord = useMemo(
    () =>
      Object.entries(apricot.collateral).find(
        ([key, value]) => key === APT_MINT.toBase58() && value.deposit.amount > 0,
      ),
    [apricot],
  );

  useEffect(() => setApricotStaking([]), [connected]);

  useDeepCompareEffect(() => {
    const apricotStakingPosition = apricotStakingRecord ? apricotStakingRecord[1] : undefined;
    if (connected && marketsInitialised && publicKey && apricotStakingPosition && Object.values(apricot).length > 0) {
      const aptPrice = priceInUSD(APT_MINT.toBase58()) ?? 0;
      setApricotStaking([
        {
          kind: "Apricot" as const,
          link: "https://app.apricot.one/",
          apy: apricotStakingPosition.deposit.apy ?? 0,
          stakingMint: APT_MINT,
          rewardMint: APT_MINT,
          pendingReward: null,
          pendingRewardUSD: null,
          amountStaked: apricotStakingPosition.deposit.amount,
          valueInUSD: aptPrice * apricotStakingPosition.deposit.amount,
        },
      ]);
    }
  }, [connected, connection, publicKey, marketsInitialised, priceInUSD, apricot, apricotStakingRecord]);
  return apricotStaking;
};
